import React, { useState, useEffect } from 'react';
import { formPages, getFormPagesForVisaType } from '../../utils/formConfig';
import { submitAllFormData, getSubmissionSummary, canSubmit } from '../../utils/dataSubmitter';
import formDataStorage from '../../utils/formDataStorage';
import { Check, AlertCircle, Send, Loader2, Image as ImageIcon } from 'lucide-react';
import ChangePhotoDialog from './ChangePhotoDialog';
import formManagementService from '../../services/formManagementService.js';
import { isValidObjectId, extractObjectIdFromPath } from '../../utils/objectIdManager.js';

// 侧边栏组件
const Sidebar = ({ 
  currentPageId, 
  onPageChange, 
  formStatuses = {},
  currentFormData = {},
  onNotification
}) => {

  const [, setSubmissionSummary] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [currentFormPages, setCurrentFormPages] = useState(formPages);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [showChangePhoto, setShowChangePhoto] = useState(false);

  // 获取当前签证类型对应的表单页面
  useEffect(() => {
    const travelData = formDataStorage.loadFormData('travel');
    let visaType = null;
    
    // 检查签证类型
    if (travelData?.purpose?.length > 0) {
      const firstPurpose = travelData.purpose[0];
      if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
        visaType = 'F';
      } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
        visaType = 'F2';
      }
    }
    
    const pages = getFormPagesForVisaType(visaType);
    setCurrentFormPages(pages);
    

    
    // 更新提交摘要以确保数据同步
    updateSubmissionSummary();
  }, [formStatuses]); // 依赖formStatuses变化来重新检查


  // 获取页面样式
  const getPageStyles = (pageId, isActive) => {
    const status = formStatuses[pageId];
    
    if (isActive) {
      return 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg';
    } else if (status === 'completed') {
      return 'bg-gradient-to-r from-green-50 to-green-100 text-green-800 hover:from-green-100 hover:to-green-200 border-l-4 border-green-500';
    } else if (status === 'partial') {
      return 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 hover:from-yellow-100 hover:to-yellow-200 border-l-4 border-yellow-500';
    } else {
      return 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';
    }
  };

  // 获取状态指示器
  const getStatusIndicator = (pageId) => {
    const status = formStatuses[pageId];
    
    if (status === 'completed') {
      return <Check className="w-4 h-4 text-green-600" />;
    } else if (status === 'partial') {
      return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    } else {
      return <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>;
    }
  };

  // 获取数字样式
  const getNumberStyles = (pageId, isActive) => {
    const status = formStatuses[pageId];
    
    if (isActive) {
      return 'bg-white text-blue-600 font-bold';
    } else if (status === 'completed') {
      return 'bg-green-600 text-white font-bold';
    } else if (status === 'partial') {
      return 'bg-yellow-600 text-white font-bold';
    } else {
      return 'bg-gray-200 text-gray-600';
    }
  };



  // 更新提交摘要
  const updateSubmissionSummary = () => {
    const summary = getSubmissionSummary();
    setSubmissionSummary(summary);
  };

  // 保存表单数据
  const handleSave = async () => {
    setIsSaving(true);

    try {
      // 先保存当前页面的数据
      if (currentFormData && Object.keys(currentFormData).length > 0) {
        formDataStorage.saveFormData(currentPageId, currentFormData);
      }
      // 保存所有表单数据到数据库
      const { saveAllFormData } = await import('../../services/formManagementService.js');
      const result = await saveAllFormData();
      setSaveResult(result);
      if (result.success) {
        if (onNotification) {
          onNotification({
            type: 'success',
            message: '表单数据保存成功',
            formId: currentPageId
          });
        }
      } else {
        if (onNotification) {
          onNotification({
            type: 'error',
            message: `保存失败: ${result.message}`,
            formId: currentPageId
          });
        }
      }
    } catch (error) {
      const errorResult = {
        success: false,
        message: `保存出错: ${error.message}`
      };
      setSaveResult(errorResult);
      if (onNotification) {
        onNotification({
          type: 'error',
          message: errorResult.message,
          formId: currentPageId
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 仅允许在草稿/进行中/失败状态下更换照片
  const canChangePhoto = (() => {
    try {
      const status = formDataStorage.loadFormData('status') || 'draft';
      return !['queued','processing','submitted','completed'].includes(status);
    } catch(e) {
      return true;
    }
  })();

  const handlePhotoChanged = (photo) => {
    try {
      formDataStorage.saveFormData('applicant_photo', photo);
      if (onNotification) {
        onNotification({ type: 'success', message: '照片已更新', formId: currentPageId });
      }
    } catch(e) {}
  };

  // 获取有效的FormId（避免loadFormData返回{}导致前端校验失败）
  const resolveFormId = () => {
    const candidates = [
      formDataStorage.loadFormData('form_id'),
      formDataStorage.loadFormData('_id'),
      formDataStorage.loadFormData('id')
    ];
    for (const v of candidates) {
      if (typeof v === 'string' && isValidObjectId(v)) return v;
    }
    // 兜底：从URL提取
    const fromPath = extractObjectIdFromPath(window.location.pathname);
    if (fromPath && isValidObjectId(fromPath)) return fromPath;
    // 兼容 /form/:id 路径
    try {
      const m = window.location.pathname.match(/\/form\/([0-9a-fA-F]{24})/);
      if (m && isValidObjectId(m[1])) return m[1];
    } catch(e) {}
    return '';
  };

  // 处理表单提交
  const handleSubmit = async () => {
    // 防止重复提交
    if (isSubmitting) {
      console.warn('⚠️ 提交正在进行中，忽略重复点击');
      return;
    }
    setIsSubmitting(true);
    setSubmissionResult(null);
    try {
      const result = await submitAllFormData();
      setSubmissionResult(result);
    } catch (error) {
      setSubmissionResult({
        success: false,
        message: `提交出错: ${error.message}`
      });
      
      // 错误消息5秒后清除
      setTimeout(() => {
        setSubmissionResult(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };



  // 监听表单状态变化，更新提交摘要
  useEffect(() => {
    updateSubmissionSummary();
  }, [formStatuses]);

  // 检查是否可以提交
  const canSubmitForms = canSubmit();

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-gray-50 border-r border-gray-200 shadow-lg flex flex-col">
      {/* 标题 */}
      <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-700">
        <h2 className="text-xl font-bold text-white mb-2">DS-160中文表单</h2>
        <p className="text-blue-100 text-sm">美国签证申请表</p>
      </div>

      {/* 页面列表 - 可滚动区域 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {(() => {
          // 使用根据签证类型动态获取的页面列表
          const filteredPages = currentFormPages;
          
          return filteredPages.map((page, index) => {
            const isActive = currentPageId === page.id;
            const Icon = page.icon;
            
            return (
              <button
                key={page.id}
                onClick={() => onPageChange(page.id)}
                className={`w-full text-left p-4 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${getPageStyles(page.id, isActive)}`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${getNumberStyles(page.id, isActive)}`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm leading-tight">{page.title}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon className="w-4 h-4" />
                    {getStatusIndicator(page.id)}
                  </div>
                </div>
              </button>
            );
          });
        })()}
      </div>

      {/* 提交区域 */}
      <div className="p-4 bg-white border-t border-gray-200">
        {/* 更换照片按钮（仅允许可编辑状态） */}
        <button
          onClick={() => setShowChangePhoto(true)}
          disabled={!canChangePhoto}
          className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md mb-3 ${
            canChangePhoto
              ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <ImageIcon className="w-4 h-4 mr-2" />
          更换照片
        </button>

        {/* 保存结果显示 */}
        {saveResult && (
          <div className={`mb-4 p-3 rounded-lg border ${
            saveResult.success 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="text-xs font-medium">
              {saveResult.success ? '💾 ' : '❌ '}
              {saveResult.message}
            </div>
          </div>
        )}

        {/* 提交结果显示 */}
        {submissionResult && (
          <div className={`mb-4 p-3 rounded-lg border ${
            submissionResult.success 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="text-xs font-medium">
              {submissionResult.success ? '✅ ' : '❌ '}
              {submissionResult.message}
            </div>
          </div>
        )}

        {/* 保存按钮 */}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md mb-3 ${
            isSaving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              保存中...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              保存
            </>
          )}
        </button>

        {/* 提交按钮 */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmitForms || isSubmitting}
          className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md ${
            canSubmitForms && !isSubmitting
              ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              正在提交...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              提交申请
            </>
          )}
        </button>

        {/* 提交说明 */}
        <div className="mt-2 text-xs text-gray-500 text-center">
          <div>
            {canSubmitForms 
              ? '✅ 所有必填项已完成，可以提交' 
              : '⚠️ 请先完成所有必填项再提交'
            }
          </div>
        </div>
      </div>

      {/* 更换照片弹窗 */}
      <ChangePhotoDialog
        isOpen={showChangePhoto}
        onClose={() => setShowChangePhoto(false)}
        onChanged={handlePhotoChanged}
        formService={formManagementService}
        formId={resolveFormId()}
      />
    </div>
  );
};

export default Sidebar;
